import React, { useEffect, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import HowToVoteOutlinedIcon from '@mui/icons-material/HowToVoteOutlined'
import RestaurantMenuOutlinedIcon from '@mui/icons-material/RestaurantMenuOutlined'
import IosShareOutlinedIcon from '@mui/icons-material/IosShareOutlined'
import { Helmet } from 'react-helmet'
import { logEvent } from '../../../analytics/analytics'
import { readPlannerDraft } from '../../../helpers/planner-draft'
import { ROUTES } from '../../../helpers/routes-helper'
import useAppUtilities from '../../../hooks/useAppUtilities'
import { usePlannerMode } from '../../../context/PlannerModeContext'
import PlannerSessionsList from './PlannerSessionsList'

/** The three steps, in the product's own voice. */
const STEPS = [
  {
    icon: RestaurantMenuOutlinedIcon,
    title: 'Pick some restaurants',
    body: 'Browse like normal and tap the group icon on anything that looks good.',
  },
  {
    icon: IosShareOutlinedIcon,
    title: 'Share one link',
    body: 'No accounts, no downloads. Your friends just open it.',
  },
  {
    icon: HowToVoteOutlinedIcon,
    title: 'Everyone votes',
    body: "The group's pick rises to the top. Decide in minutes, not group chats.",
  },
]

/**
 * The Planner tab landing (VARIANT arm) — what a curious user sees instead of a form.
 *
 * The old screen opened with five fields, one of them required, before a planner could exist:
 * 658 users reached it and 25 created one. This screen asks for nothing. It explains what Planner
 * is, why it helps, and offers one primary action — start picking restaurants — which creates a
 * draft planner behind the scenes and drops the user straight back into browsing with planner mode
 * on. Configuration (occasion, date, cuisine, party size, an exact location) is deferred to the
 * planner page, where it is optional and only asked for when it actually buys something.
 *
 * Secondary paths: resume an unfinished draft, open an existing planner, or search a location.
 */
const PlannerLanding: React.FC = () => {
  const { navigate, theme } = useAppUtilities()
  const planner = usePlannerMode()
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sessionCount, setSessionCount] = useState(0)
  const loggedRef = useRef(false)
  const hasDraft = planner?.hasDraft ?? readPlannerDraft() !== null

  // The entry denominator for entry -> create conversion. Guarded by a ref: this view re-renders as
  // the sessions list resolves, and a double-counted denominator would understate conversion.
  useEffect(() => {
    if (loggedRef.current) return
    loggedRef.current = true
    logEvent('session_landing_viewed', {
      has_draft: hasDraft,
      has_sessions: sessionCount > 0,
      screen_name: 'planner_landing',
    })
  }, [hasDraft, sessionCount])

  const handleStart = async (): Promise<void> => {
    setError(null)
    setStarting(true)
    try {
      const token = await planner?.startPlanner('planner_landing')
      if (!token) {
        setError('Could not start a planner. Please try again.')
        return
      }
      // Straight back into browsing with planner mode on — the value (restaurants) comes first and
      // the persistent planner bar carries the count and the share action from here.
      navigate(ROUTES.HOME)
    } finally {
      setStarting(false)
    }
  }

  const handleResume = async (): Promise<void> => {
    setError(null)
    setStarting(true)
    try {
      const token = await planner?.resumePlanner('planner_landing')
      if (!token) {
        // The draft's session expired or was deleted — the context has already cleared the stale
        // pointer, so fall through to starting fresh rather than showing a dead button.
        setError('That planner is no longer available. Start a new one.')
        return
      }
      navigate(ROUTES.HOME)
    } finally {
      setStarting(false)
    }
  }

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 3, md: 6 } }}>
      <Helmet>
        <title>Resactly | Plan a dinner with friends</title>
        <meta
          name="description"
          content="Pick some restaurants. Share one link. Everyone votes. Plan a group dinner in seconds — no account needed."
        />
      </Helmet>

      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <GroupsOutlinedIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Plan a dinner with friends
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Pick some restaurants. Share one link. Everyone votes — no account
          needed.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Resume takes precedence over starting fresh: an unfinished planner is the highest-intent
          thing this user has, and losing it is how a planner quietly dies. */}
      {hasDraft && (
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            size="large"
            fullWidth
            disabled={starting}
            onClick={() => {
              void handleResume()
            }}
            sx={{ textTransform: 'none', borderRadius: 2, py: 1.25 }}
          >
            {starting ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              'Pick up where you left off'
            )}
          </Button>
        </Box>
      )}

      <Box
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: 'background.paper',
          mb: 3,
        }}
      >
        <Stack spacing={2.5}>
          {STEPS.map(({ icon: Icon, title, body }) => (
            <Stack key={title} direction="row" spacing={2} alignItems="flex-start">
              <Box
                sx={{
                  flexShrink: 0,
                  width: 40,
                  height: 40,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: theme.palette.primary[50] ?? 'action.hover',
                  color: theme.palette.primary.main,
                }}
              >
                <Icon fontSize="small" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {body}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Stack>

        <Button
          variant={hasDraft ? 'outlined' : 'contained'}
          size="large"
          fullWidth
          disabled={starting}
          onClick={() => {
            void handleStart()
          }}
          sx={{ mt: 3, textTransform: 'none', borderRadius: 2, py: 1.25 }}
        >
          {starting ? (
            <CircularProgress size={22} color="inherit" />
          ) : hasDraft ? (
            'Start a new planner'
          ) : (
            'Start picking restaurants'
          )}
        </Button>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'center', mt: 1 }}
        >
          You can add the date, place and party size later — or never.
        </Typography>
      </Box>

      {/* Secondary: search a specific place to pick from. */}
      <Stack alignItems="center" sx={{ mb: 3 }}>
        <Button
          variant="text"
          onClick={() => navigate(ROUTES.SEARCH)}
          sx={{ textTransform: 'none' }}
        >
          Search a location instead
        </Button>
      </Stack>

      {sessionCount > 0 && <Divider sx={{ mb: 3 }} />}
      <PlannerSessionsList
        screenName="planner_landing"
        onLoaded={setSessionCount}
      />
    </Container>
  )
}

export default PlannerLanding
